import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  CheckCircle2,
  Calculator,
  ScanLine,
  TrendingUp,
  BarChart3,
  Scissors,
  QrCode,
  Scale,
  Clock,
  XCircle,
  AlertTriangle,
  Eye,
  FileSpreadsheet,
  Star,
  BookOpen,
  Zap,
  Shield,
  Play,
} from 'lucide-react';

/* ------------------------------------------------------------------ */
/*  FAQs                                                               */
/* ------------------------------------------------------------------ */
const faqs = [
  {
    q: '¿Qué es un software de escandallos?',
    a: 'Un software de escandallos es una aplicación que automatiza el cálculo del coste de producción de cada plato de tu restaurante. En lugar de usar hojas de Excel manuales, el software calcula automáticamente el coste total de cada receta a partir de los ingredientes, cantidades y precios de compra. Cuando un precio cambia, todos los escandallos se actualizan al instante, manteniendo tu food cost siempre al día.',
  },
  {
    q: '¿Cuánto cuesta un software de escandallo?',
    a: 'Los precios varían según las funcionalidades. Las soluciones básicas de hojas de cálculo son gratuitas pero requieren horas de trabajo manual. Los softwares profesionales como PLEKO van desde 49€/mes (plan BASIC para 1 usuario) hasta 149€/mes (plan SCALE para hasta 10 usuarios con multi-restaurante). La mayoría de restaurantes recuperan la inversión en el primer mes gracias al ahorro en food cost y tiempo.',
  },
  {
    q: '¿PLEKO funciona para bares y cafeterías?',
    a: 'Sí. PLEKO funciona para cualquier negocio de hostelería: restaurantes, bares, cafeterías, pastelerías, caterings, hoteles y comedores colectivos. El sistema de escandallos se adapta a todo tipo de elaboraciones: platos, cócteles, cafés especiales, bollería, menús del día y elaboraciones base (salsas, caldos, masas).',
  },
  {
    q: '¿Puedo importar mis recetas desde Excel?',
    a: 'Sí. PLEKO incluye un asistente de importación desde Excel en 3 pasos. Sube tu archivo Excel con ingredientes y precios, mapea las columnas y PLEKO importa todo automáticamente. También puedes escanear facturas con OCR para dar de alta ingredientes y precios directamente desde el albarán del proveedor.',
  },
  {
    q: '¿Cómo funciona el OCR de facturas?',
    a: 'El OCR de PLEKO utiliza inteligencia artificial (GPT-4 Vision) para leer facturas y albaranes de proveedores. Haces una foto o subes un PDF, la IA extrae los productos, cantidades y precios, y actualiza automáticamente los precios de tus ingredientes. Todos los escandallos se recalculan al instante con los nuevos precios.',
  },
  {
    q: '¿Necesito conocimientos técnicos para usar PLEKO?',
    a: 'No. PLEKO está diseñado para profesionales de la hostelería, no para informáticos. La interfaz es intuitiva y visual: añades ingredientes, creas recetas y PLEKO hace los cálculos. Incluye onboarding guiado, 22 vídeos tutoriales y soporte en español. La mayoría de usuarios están operativos en menos de una hora.',
  },
  {
    q: '¿Puedo probarlo gratis antes de pagar?',
    a: 'Sí. PLEKO ofrece 14 días de prueba gratuita sin necesidad de tarjeta de crédito. Durante la prueba tienes acceso completo a todas las funcionalidades del plan que elijas. Si no te convence, simplemente no activas la suscripción y no se te cobra nada.',
  },
  {
    q: '¿Qué soporte incluye PLEKO?',
    a: 'Todos los planes incluyen soporte por email y chat en español. Los planes PRO y SCALE incluyen soporte prioritario con respuesta en menos de 4 horas laborables. Además, tienes acceso a 22 vídeos tutoriales paso a paso, una sección de ayuda completa dentro de la aplicación y onboarding personalizado al registrarte.',
  },
];

/* ------------------------------------------------------------------ */
/*  Structured data (JSON-LD)                                          */
/* ------------------------------------------------------------------ */
const jsonLd = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'SoftwareApplication',
      name: 'PLEKO — Software de Escandallo',
      applicationCategory: 'BusinessApplication',
      applicationSubCategory: 'Restaurant Management',
      operatingSystem: 'Web',
      description:
        'Software de escandallo para restaurantes y hostelería. Calcula el coste de tus platos automáticamente con OCR de facturas, precios de mercado y control de food cost en tiempo real.',
      url: 'https://pleko.app/software-escandallo',
      offers: [
        { '@type': 'Offer', price: '49', priceCurrency: 'EUR', name: 'BASIC', description: 'Para restaurantes independientes — 1 usuario' },
        { '@type': 'Offer', price: '79', priceCurrency: 'EUR', name: 'PRO', description: 'Hasta 3 usuarios + control horario' },
        { '@type': 'Offer', price: '149', priceCurrency: 'EUR', name: 'SCALE', description: 'Hasta 10 usuarios + multi-restaurante' },
      ],
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: '4.8',
        ratingCount: '47',
        bestRating: '5',
        worstRating: '1',
      },
      screenshot: 'https://pleko.app/og-image.png',
      featureList:
        'Escandallos automáticos, OCR de facturas, Food cost en tiempo real, Precios de mercado, Mermas inteligentes, Carta digital con QR, Escalador de raciones, Control horario',
    },
    {
      '@type': 'FAQPage',
      mainEntity: faqs.map((faq) => ({
        '@type': 'Question',
        name: faq.q,
        acceptedAnswer: { '@type': 'Answer', text: faq.a },
      })),
    },
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'PLEKO', item: 'https://pleko.app/' },
        { '@type': 'ListItem', position: 2, name: 'Software de Escandallo', item: 'https://pleko.app/software-escandallo' },
      ],
    },
    {
      '@type': 'Organization',
      name: 'PLEKO',
      url: 'https://pleko.app',
      logo: 'https://pleko.app/favicon.svg',
      sameAs: ['https://www.linkedin.com/company/pleko'],
    },
  ],
};

/* ------------------------------------------------------------------ */
/*  Features grid data                                                 */
/* ------------------------------------------------------------------ */
const features = [
  {
    icon: Calculator,
    title: 'Escandallos automaticos',
    desc: 'Añade ingredientes con cantidades y PLEKO calcula el coste total del plato, el food cost y el PVP sugerido. Cambia un precio y todas las recetas se actualizan al instante.',
  },
  {
    icon: ScanLine,
    title: 'OCR de facturas con IA',
    desc: 'Escanea facturas y albaranes con tu movil. La IA extrae productos y precios, actualiza tus ingredientes y recalcula todos los escandallos automaticamente.',
  },
  {
    icon: TrendingUp,
    title: 'Food cost en tiempo real',
    desc: 'Dashboard con semaforo verde/amarillo/rojo por receta. Detecta al instante que platos estan por encima de tu objetivo de food cost y actua antes de perder dinero.',
  },
  {
    icon: BarChart3,
    title: 'Precios de mercado',
    desc: 'Compara los precios de tus proveedores con los de Mercabarna, Mercamadrid y otros mercados mayoristas. Descubre si estas pagando de mas.',
  },
  {
    icon: Scissors,
    title: 'Mermas inteligentes',
    desc: 'Configura mermas por ingrediente y tipo (limpieza, coccion, almacenamiento). PLEKO aplica las mermas automaticamente a todos los escandallos.',
  },
  {
    icon: QrCode,
    title: 'Carta digital con QR',
    desc: 'Diseña tu carta digital y vinculala a tus recetas. PLEKO calcula el PVP sugerido de cada plato segun tu food cost objetivo. Genera el QR en un clic.',
  },
  {
    icon: Scale,
    title: 'Escalador de raciones',
    desc: 'Necesitas producir para 50 personas en vez de 4? Escala cualquier receta con un clic y obtén la lista de ingredientes ajustada. Ideal para eventos y caterings.',
  },
  {
    icon: Clock,
    title: 'Control horario integrado',
    desc: 'Cumple con el RD-ley 8/2019 sin salir de PLEKO. Tus empleados fichan desde la app y tu tienes el registro legal automatico. Incluido en planes PRO y SCALE.',
  },
];

/* ------------------------------------------------------------------ */
/*  Pain points data                                                   */
/* ------------------------------------------------------------------ */
const painPoints = [
  {
    icon: FileSpreadsheet,
    title: 'Horas perdidas con Excel',
    desc: 'Cada vez que cambia el precio de un ingrediente, tienes que abrir el Excel, buscar todas las recetas que lo usan y actualizar una por una. Con 50 recetas y 200 ingredientes, eso son horas cada semana.',
  },
  {
    icon: AlertTriangle,
    title: 'Errores en los calculos',
    desc: 'Un decimal mal puesto, una formula rota, un ingrediente olvidado. Los errores manuales pasan desapercibidos y afectan directamente a tu margen. Un error del 2% en food cost puede suponer miles de euros al año.',
  },
  {
    icon: TrendingUp,
    title: 'Precios que cambian cada semana',
    desc: 'El precio del salmon ha subido un 15% este mes. El aceite de oliva fluctua cada semana. Sin un sistema que actualice los escandallos en tiempo real, tu carta tiene precios calculados con datos obsoletos.',
  },
  {
    icon: Eye,
    title: 'Sin vision de tu food cost real',
    desc: 'Sabes cuanto facturas, pero no cuanto te cuesta cada plato que sirves. Sin escandallos actualizados es imposible saber si estas ganando o perdiendo dinero con cada receta de tu carta.',
  },
];

/* ------------------------------------------------------------------ */
/*  Comparison table data                                              */
/* ------------------------------------------------------------------ */
const comparisonRows = [
  ['Escandallos automaticos', 'Manual', 'Parcial', true],
  ['OCR de facturas con IA', false, false, true],
  ['Precios de mercado mayorista', false, false, true],
  ['Multi-usuario con roles', false, 'Parcial', true],
  ['Actualizacion de precios en cascada', false, 'Parcial', true],
  ['Food cost en tiempo real', false, 'Parcial', true],
  ['Mermas configurables por ingrediente', 'Manual', 'Parcial', true],
  ['Carta digital con QR', false, false, true],
  ['Escalador de raciones', false, false, true],
  ['Control horario legal', false, false, true],
  ['Soporte en español', false, true, true],
  ['Precio', 'Gratis (+ horas)', 'Desde 80€/mes', 'Desde 49€/mes'],
] as const;

/* ------------------------------------------------------------------ */
/*  Testimonials                                                       */
/* ------------------------------------------------------------------ */
const testimonials = [
  {
    name: 'Jordi Castellano',
    role: 'Chef propietario, La Boqueria Gastrobar (Barcelona)',
    text: 'Antes tardaba 4 horas en actualizar mis escandallos cada vez que cambiaban los precios. Con PLEKO lo hago en 15 minutos. El OCR de facturas es una maravilla.',
    stars: 5,
  },
  {
    name: 'Laura Fernandez',
    role: 'Directora, Catering Delicia (Madrid)',
    text: 'El OCR de facturas me ha cambiado la vida. Escaneo el albaran del proveedor con el movil y los precios se actualizan solos. Todos mis escandallos siempre estan al dia.',
    stars: 5,
  },
  {
    name: 'Marc Puig',
    role: 'Jefe de cocina, Hotel Marina (Costa Brava)',
    text: 'Por fin tengo el food cost en tiempo real. En 3 meses he reducido un 8% los costes de materia prima. El semaforo por receta me permite actuar rapido cuando algo se dispara.',
    stars: 5,
  },
];

/* ------------------------------------------------------------------ */
/*  Steps                                                              */
/* ------------------------------------------------------------------ */
const steps = [
  {
    step: '1',
    title: 'Sube tus ingredientes',
    desc: 'Importa desde Excel, escanea facturas con OCR o añade manualmente. PLEKO guarda precios, unidades, proveedores y mermas de cada ingrediente.',
  },
  {
    step: '2',
    title: 'Crea tus recetas',
    desc: 'Añade ingredientes con cantidades y PLEKO calcula automáticamente el escandallo completo: coste total, coste por ración, food cost y PVP sugerido.',
  },
  {
    step: '3',
    title: 'Controla y optimiza',
    desc: 'Monitoriza el food cost de toda tu carta en tiempo real. Detecta recetas no rentables, compara precios de mercado y toma decisiones con datos, no con intuición.',
  },
];

/* ------------------------------------------------------------------ */
/*  Plans                                                              */
/* ------------------------------------------------------------------ */
const plans = [
  {
    name: 'BASIC',
    price: '49',
    desc: 'Restaurantes independientes',
    features: [
      'Ingredientes y recetas ilimitadas',
      'Escandallos automáticos',
      '15 escaneos OCR/mes',
      'Carta digital con QR',
      'Dashboard de food cost',
      '1 usuario',
    ],
  },
  {
    name: 'PRO',
    price: '79',
    desc: 'El más completo',
    popular: true,
    features: [
      'Todo de BASIC',
      '60 escaneos OCR/mes',
      'Control horario legal',
      'Laboratorio gastronómico (R&D)',
      'Soporte prioritario',
      'Hasta 3 usuarios',
    ],
  },
  {
    name: 'SCALE',
    price: '149',
    desc: 'Grupos y multi-restaurante',
    features: [
      'Todo de PRO',
      '200 escaneos OCR/mes',
      'Precios de mercado mayorista',
      'Multi-restaurante',
      'Laboratorio avanzado',
      'Hasta 10 usuarios',
    ],
  },
];

/* ------------------------------------------------------------------ */
/*  Helper                                                             */
/* ------------------------------------------------------------------ */
function CellIcon({ value }: { value: boolean | string }) {
  if (value === true) return <CheckCircle2 className="w-5 h-5 text-green-500 mx-auto" />;
  if (value === false) return <XCircle className="w-5 h-5 text-red-400 mx-auto" />;
  return <span className="text-slate-600 text-sm">{value}</span>;
}

/* ================================================================== */
/*  COMPONENT                                                          */
/* ================================================================== */
export default function SoftwareEscandalloPage() {
  return (
    <>
      <Helmet>
        <title>Software de Escandallo para Restaurantes — Mejor Herramienta 2026 | PLEKO</title>
        <meta
          name="description"
          content="El mejor software de escandallo para hostelería. Calcula el coste de tus platos automáticamente con OCR de facturas, precios de mercado y control de food cost en tiempo real. Prueba gratis 14 días."
        />
        <meta
          name="keywords"
          content="software escandallo, software de escandallo, programa escandallo, herramienta escandallo, app escandallo, software escandallos restaurante, software calcular food cost, programa food cost restaurante, software hostelería costes"
        />
        <link rel="canonical" href="https://pleko.app/software-escandallo" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://pleko.app/software-escandallo" />
        <meta property="og:title" content="Software de Escandallo para Restaurantes — Mejor Herramienta 2026 | PLEKO" />
        <meta
          property="og:description"
          content="Calcula el coste de tus platos automáticamente con OCR de facturas, precios de mercado y control de food cost en tiempo real. Prueba gratis 14 días."
        />
        <meta property="og:image" content="https://pleko.app/og-image.png" />
        <meta property="og:locale" content="es_ES" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Software de Escandallo para Restaurantes — Mejor Herramienta 2026 | PLEKO" />
        <meta
          name="twitter:description"
          content="Calcula el coste de tus platos automáticamente con OCR de facturas, precios de mercado y control de food cost en tiempo real. Prueba gratis 14 días."
        />
        <meta name="twitter:image" content="https://pleko.app/og-image.png" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <div className="bg-white">
        {/* ======================== HERO ======================== */}
        <section className="bg-gradient-to-b from-slate-900 to-slate-800 py-16 lg:py-24 text-white">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-blue-500/20 text-blue-300 rounded-full text-sm font-medium mb-6">
              <Star className="w-4 h-4" />
              El software de escandallos #1 en España
            </span>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold mb-6 tracking-tight leading-tight">
              Software de Escandallo para Restaurantes —{' '}
              <span className="text-blue-400">Calcula Costes Automaticamente</span>
            </h1>

            <p className="text-xl text-slate-300 mb-8 max-w-3xl mx-auto leading-relaxed">
              Deja de perder horas con Excel. Calcula el food cost de toda tu carta en minutos con escandallos automaticos,
              OCR de facturas y precios de mercado en tiempo real. El software que usan los restaurantes mas rentables de España.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/register"
                className="inline-flex items-center gap-2 px-8 py-4 bg-blue-500 text-white font-bold rounded-xl hover:bg-blue-400 transition-colors shadow-lg shadow-blue-500/25"
              >
                Probar gratis 14 dias
                <ArrowRight className="w-5 h-5" />
              </Link>
              <a
                href="#video"
                className="inline-flex items-center gap-2 px-8 py-4 border border-slate-600 text-slate-300 font-semibold rounded-xl hover:border-slate-400 hover:text-white transition-colors"
              >
                <Play className="w-4 h-4" />
                Ver demo en video
              </a>
            </div>

            <p className="mt-5 text-slate-400 text-sm flex items-center justify-center gap-4 flex-wrap">
              <span className="flex items-center gap-1"><Shield className="w-3.5 h-3.5" /> Sin tarjeta de credito</span>
              <span>14 dias gratis</span>
              <span>Cancela cuando quieras</span>
            </p>

            {/* Social proof stats */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-12 max-w-2xl mx-auto">
              {[
                { stat: '28,5%', label: 'Food cost medio', sub: 'de nuestros usuarios' },
                { stat: '3h/semana', label: 'Tiempo ahorrado', sub: 'en gestion de costes' },
                { stat: '<2 min', label: 'Crear una receta', sub: 'con coste calculado' },
              ].map(({ stat, label, sub }) => (
                <div key={stat} className="text-center p-4 bg-white/5 rounded-xl border border-white/10">
                  <p className="text-2xl font-bold text-blue-400">{stat}</p>
                  <p className="text-sm text-slate-300 font-medium">{label}</p>
                  <p className="text-xs text-slate-500">{sub}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ======================== PAIN POINTS ======================== */}
        <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              ¿Por que necesitas un software de escandallos?
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              El 60% de los restaurantes que cierran en los primeros 3 años no llevaban un control de costes adecuado.
              Estos son los problemas mas comunes que resuelve un software de escandallos:
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {painPoints.map(({ icon: Icon, title, desc }) => (
              <div key={title} className="flex gap-4 p-6 bg-red-50 border border-red-100 rounded-xl">
                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                  <Icon className="w-5 h-5 text-red-500" />
                </div>
                <div>
                  <h3 className="font-bold text-slate-800 mb-1">{title}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">{desc}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-10 bg-blue-50 border border-blue-100 rounded-xl p-6 text-center">
            <p className="text-blue-800 font-semibold text-lg mb-2">
              Un restaurante con 50 recetas y 200 ingredientes pierde entre 4 y 8 horas semanales actualizando escandallos en Excel.
            </p>
            <p className="text-blue-600">
              Con PLEKO, esas horas se reducen a <strong>minutos</strong>. Y los calculos son <strong>siempre exactos</strong>.
            </p>
          </div>
        </section>

        {/* ======================== FEATURES GRID ======================== */}
        <section className="bg-slate-50 py-16 lg:py-20">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
                Que hace PLEKO por tu restaurante
              </h2>
              <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                Todo lo que necesitas para controlar los costes de tu cocina en una sola plataforma.
                Sin Excel, sin errores, sin perder tiempo.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              {features.map(({ icon: Icon, title, desc }) => (
                <div
                  key={title}
                  className="flex gap-4 p-5 bg-white border border-slate-200 rounded-xl hover:border-blue-200 hover:shadow-sm transition-all"
                >
                  <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                    <Icon className="w-5 h-5 text-blue-500" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-800 mb-1">{title}</h3>
                    <p className="text-slate-600 text-sm leading-relaxed">{desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8 text-center">
              <Link
                to="/como-funciona"
                className="inline-flex items-center gap-2 text-blue-600 font-semibold hover:text-blue-700 hover:underline"
              >
                Ver todas las funcionalidades en detalle
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </section>

        {/* ======================== COMPARISON TABLE ======================== */}
        <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              PLEKO vs Excel vs Otras Herramientas
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Compara las opciones del mercado y descubre por que PLEKO es la eleccion de los restaurantes que quieren
              resultados reales sin complicaciones.
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr>
                  <th className="p-4 text-left font-semibold text-slate-700 bg-slate-50 rounded-tl-xl">Funcionalidad</th>
                  <th className="p-4 text-center font-semibold text-slate-700 bg-slate-50">Excel / Plantillas</th>
                  <th className="p-4 text-center font-semibold text-slate-700 bg-slate-50">Otros software</th>
                  <th className="p-4 text-center font-bold text-blue-700 bg-blue-50 rounded-tr-xl">PLEKO</th>
                </tr>
              </thead>
              <tbody>
                {comparisonRows.map(([feature, excel, otros, pleko], i) => (
                  <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}>
                    <td className="p-4 border-b border-slate-100 font-medium text-slate-700">{feature}</td>
                    <td className="p-4 border-b border-slate-100 text-center"><CellIcon value={excel} /></td>
                    <td className="p-4 border-b border-slate-100 text-center"><CellIcon value={otros} /></td>
                    <td className="p-4 border-b border-slate-100 text-center bg-blue-50/30"><CellIcon value={pleko} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="text-center text-slate-500 text-sm mt-4">
            * Comparativa basada en funcionalidades publicadas por escandallos.es, IFN Software y soluciones genericas de gestion hostelera.
          </p>
        </section>

        {/* ======================== HOW IT WORKS ======================== */}
        <section className="bg-gradient-to-b from-blue-50 to-white py-16 lg:py-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
                Como funciona en 3 pasos
              </h2>
              <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                De cero a tener todos tus escandallos calculados en menos de una hora.
                Sin formacion previa, sin complicaciones tecnicas.
              </p>
            </div>

            <div className="space-y-6">
              {steps.map(({ step, title, desc }) => (
                <div key={step} className="flex gap-5 p-6 bg-white rounded-xl border border-slate-200 shadow-sm">
                  <div className="flex-shrink-0 w-12 h-12 rounded-full bg-blue-500 text-white font-bold flex items-center justify-center text-lg shadow-lg shadow-blue-200">
                    {step}
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-slate-800 mb-1">{title}</h3>
                    <p className="text-slate-600 leading-relaxed">{desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-10 text-center">
              <Link
                to="/register"
                className="inline-flex items-center gap-2 px-8 py-4 bg-blue-500 text-white font-bold rounded-xl hover:bg-blue-600 transition-colors shadow-lg shadow-blue-200"
              >
                Empezar ahora — es gratis
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          </div>
        </section>

        {/* ======================== VIDEO DEMO ======================== */}
        <section id="video" className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20 scroll-mt-20">
          <div className="text-center mb-8">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Mira PLEKO en accion
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Asi de rapido se crea un escandallo con PLEKO. Sin Excel, sin formulas, sin errores.
            </p>
          </div>

          <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-slate-200">
            <video src="/landing-demo.mp4" autoPlay muted loop playsInline className="w-full" />
          </div>
        </section>

        {/* ======================== TESTIMONIALS ======================== */}
        <section className="bg-slate-50 py-16 lg:py-20">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
                Lo que dicen nuestros usuarios
              </h2>
              <p className="text-lg text-slate-600">
                Restaurantes, caterings y hoteles que ya controlan sus costes con PLEKO.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {testimonials.map(({ name, role, text, stars }) => (
                <div key={name} className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
                  <div className="flex gap-0.5 mb-3">
                    {Array.from({ length: stars }).map((_, i) => (
                      <Star key={i} className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                    ))}
                  </div>
                  <p className="text-slate-700 italic mb-4 leading-relaxed">"{text}"</p>
                  <div>
                    <p className="font-semibold text-slate-800">{name}</p>
                    <p className="text-slate-500 text-sm">{role}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ======================== DETAILED CONTENT (SEO body) ======================== */}
        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
          <div className="prose prose-lg max-w-none">

            <h2 className="text-3xl font-bold text-slate-900 mb-6">¿Que es un software de escandallo y por que lo necesitas?</h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Un <strong>software de escandallo</strong> es una herramienta digital que automatiza el calculo del coste de produccion de cada plato, bebida o elaboracion de tu restaurante. Sustituye las hojas de calculo manuales por un sistema inteligente que mantiene todos tus escandallos actualizados en tiempo real.
            </p>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              En la hosteleria española, el <strong>escandallo</strong> es el documento clave para controlar la rentabilidad: recoge cada ingrediente con su cantidad, merma y precio de compra para calcular el <strong>coste total del plato</strong> y el <strong>food cost</strong> (porcentaje del coste sobre el precio de venta). Un food cost bien controlado — por debajo del 30% — es lo que separa a los restaurantes rentables de los que luchan por sobrevivir.
            </p>
            <p className="text-slate-700 text-lg leading-relaxed mb-8">
              El problema es que mantener los escandallos actualizados manualmente es una tarea agotadora. Cada vez que un proveedor sube el precio del salmon, la ternera o el aceite de oliva, tienes que abrir tu Excel, localizar todas las recetas afectadas y recalcular costes y precios. Con un software como PLEKO, esto ocurre <strong>automaticamente en un segundo</strong>.
            </p>

            <h2 className="text-3xl font-bold text-slate-900 mb-6">El coste oculto de no usar un software de escandallos</h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Segun datos del sector hostelero español, un restaurante medio con 40-60 recetas en carta pierde entre <strong>4 y 8 horas semanales</strong> actualizando escandallos manualmente. Eso son mas de 200 horas al año — el equivalente a 5 semanas laborales completas dedicadas a copiar numeros en un Excel.
            </p>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Pero el verdadero coste no esta en el tiempo: esta en los <strong>errores invisibles</strong>. Un ingrediente olvidado, una merma no actualizada o un precio de compra obsoleto pueden distorsionar el food cost de un plato en un 3-5%. Multiplicado por todos los servicios del mes, eso se traduce en <strong>cientos o miles de euros de margen perdido</strong> sin que te des cuenta.
            </p>
            <p className="text-slate-700 text-lg leading-relaxed mb-8">
              Un <strong>programa de escandallos</strong> como PLEKO elimina estos errores porque el calculo es siempre automatico y los precios se actualizan en cascada: cambias el precio de un ingrediente y todas las recetas que lo contienen se recalculan al instante. Sin intervención manual, sin errores, sin sorpresas a fin de mes.
            </p>

            <h2 className="text-3xl font-bold text-slate-900 mb-6">Funcionalidades clave de un buen software de escandallo</h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              No todos los programas de escandallo son iguales. Estos son los criterios que debes evaluar al elegir el software para tu restaurante:
            </p>

            <h3 className="text-2xl font-bold text-slate-800 mb-4">Calculo automatico de escandallos</h3>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Es la funcionalidad basica: añades ingredientes con cantidades a una receta y el software calcula automaticamente el coste total, el coste por racion, el food cost y el PVP sugerido. PLEKO va un paso mas alla: cuando actualizas el precio de un ingrediente (manual o via OCR), <strong>todas las recetas que lo usan se recalculan instantaneamente</strong>. Esto incluye sub-recetas (elaboraciones base como salsas, caldos o masas que se usan en multiples platos).
            </p>

            <h3 className="text-2xl font-bold text-slate-800 mb-4">OCR de facturas y albaranes</h3>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              La actualizacion de precios es el punto de dolor mas grande en la gestion de escandallos. Con el <Link to="/ocr-facturas-restaurante" className="text-blue-600 hover:underline">OCR de facturas de PLEKO</Link>, simplemente haces una foto al albaran de tu proveedor con el movil. La inteligencia artificial (GPT-4 Vision) extrae productos, cantidades y precios, y actualiza automaticamente tus ingredientes. Todos los escandallos se recalculan al instante.
            </p>

            <h3 className="text-2xl font-bold text-slate-800 mb-4">Gestion de mermas</h3>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Las <Link to="/mermas-cocina" className="text-blue-600 hover:underline">mermas</Link> son el factor que mas restaurantes olvidan en sus escandallos. Un kilo de gambas a 22€/kg con un 40% de merma (cabezas y cascaras) tiene un coste real de 36,67€/kg utilizable. PLEKO permite configurar mermas por ingrediente (limpieza, coccion, almacenamiento) y las aplica automaticamente a todos los escandallos.
            </p>

            <h3 className="text-2xl font-bold text-slate-800 mb-4">Precios de mercado mayorista</h3>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              ¿Estas pagando demasiado por el salmon? ¿El precio del tomate que te cobra tu proveedor esta por encima del mercado? PLEKO compara tus precios de compra con los de Mercabarna, Mercamadrid y otros mercados mayoristas, para que puedas <strong>negociar con datos reales</strong> y detectar proveedores que te cobran de mas.
            </p>

            <h3 className="text-2xl font-bold text-slate-800 mb-4">Semaforo de food cost</h3>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              El <Link to="/food-cost" className="text-blue-600 hover:underline">food cost</Link> de cada receta aparece con un codigo de color: <strong className="text-green-600">verde</strong> (por debajo del 30%), <strong className="text-yellow-600">amarillo</strong> (30-35%) y <strong className="text-red-600">rojo</strong> (por encima del 35%). De un vistazo sabes que platos son rentables y cuales necesitan atencion. Los umbrales son configurables por restaurante.
            </p>

            <h3 className="text-2xl font-bold text-slate-800 mb-4">Escalador de raciones</h3>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              ¿Tienes un evento para 80 personas y tu receta esta para 4 raciones? El escalador de PLEKO recalcula todas las cantidades de ingredientes al instante. Solo introduces el numero de raciones deseado y obtienes la lista de compras ajustada. Es imprescindible para caterings, eventos y produccion en grandes volumenes.
            </p>

            <h2 className="text-3xl font-bold text-slate-900 mb-6">¿Software de escandallos o Excel? La comparacion definitiva</h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Muchos restaurantes empiezan con una <Link to="/plantilla-escandallo-excel" className="text-blue-600 hover:underline">plantilla de escandallo en Excel</Link>. Es comprensible: es gratis y ya conoces la herramienta. Pero a medida que crece tu carta, Excel se convierte en una trampa de tiempo y errores.
            </p>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              <strong>El problema fundamental de Excel</strong> es que no tiene relacion automatica entre precios e ingredientes. Cuando el precio de la ternera sube de 14€/kg a 16€/kg, tienes que buscar manualmente todas las recetas que contienen ternera y actualizar cada una. Con 50+ recetas, eso implica horas de trabajo repetitivo y una alta probabilidad de errores.
            </p>
            <p className="text-slate-700 text-lg leading-relaxed mb-8">
              Con PLEKO, cambias el precio de la ternera <strong>una sola vez</strong> y todos los escandallos se actualizan en cascada en menos de un segundo. Incluido el food cost, el PVP sugerido y las alertas si alguna receta supera tu umbral objetivo. Es la diferencia entre conducir con el salpicadero apagado y tener todos los indicadores en tiempo real.
            </p>

            <h2 className="text-3xl font-bold text-slate-900 mb-6">¿Para que tipo de negocios funciona un software de escandallo?</h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Un software de escandallos no es solo para restaurantes gastronómicos. Cualquier negocio que transforme materias primas en productos alimentarios se beneficia de un control preciso de costes:
            </p>
            <ul className="space-y-3 mb-8">
              {[
                'Restaurantes independientes — controla el food cost de toda tu carta',
                'Bares y cafeterias — escandallos de tapas, cocteles y cafes especiales',
                'Caterings — calcula el coste por persona y genera presupuestos automaticos',
                'Pastelerias y obradores — escandallos por lote y por unidad',
                'Hoteles y resorts — gestion de multiples puntos de servicio',
                'Comedores colectivos (hospitales, colegios) — control de costes por menu',
                'Cadenas y grupos de restauracion — gestion multi-restaurante centralizada',
                'Dark kitchens — optimización de costes por marca virtual',
              ].map((item) => (
                <li key={item} className="flex items-start gap-3 text-slate-700">
                  <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>

            <h2 className="text-3xl font-bold text-slate-900 mb-6">¿Cuanto puedes ahorrar con un software de escandallos?</h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Vamos a hacer numeros con un restaurante tipo que factura 30.000€/mes:
            </p>
            <div className="grid md:grid-cols-2 gap-4 mb-8">
              <div className="p-5 bg-red-50 border border-red-100 rounded-xl">
                <h4 className="font-bold text-red-800 mb-2">Sin software (food cost 34%)</h4>
                <p className="text-slate-700 text-sm mb-2">Coste materia prima: 10.200€/mes</p>
                <p className="text-slate-700 text-sm mb-2">Tiempo en escandallos: 6h/semana (24h/mes)</p>
                <p className="text-slate-700 text-sm">Errores estimados: 2-3% de margen perdido</p>
              </div>
              <div className="p-5 bg-green-50 border border-green-100 rounded-xl">
                <h4 className="font-bold text-green-800 mb-2">Con PLEKO (food cost 29%)</h4>
                <p className="text-slate-700 text-sm mb-2">Coste materia prima: 8.700€/mes</p>
                <p className="text-slate-700 text-sm mb-2">Tiempo en escandallos: 30 min/semana (2h/mes)</p>
                <p className="text-slate-700 text-sm">Errores: 0% (calculo automatico)</p>
              </div>
            </div>
            <div className="bg-blue-50 border border-blue-100 rounded-xl p-6 text-center mb-8">
              <p className="text-lg font-bold text-blue-800 mb-1">Ahorro estimado: 1.500€/mes en materia prima + 22 horas de trabajo</p>
              <p className="text-blue-600 text-sm">El plan BASIC de PLEKO cuesta 49€/mes. ROI del primer mes: <strong>30x</strong></p>
            </div>
          </div>
        </section>

        {/* ======================== PRICING ======================== */}
        <section className="bg-slate-50 py-16 lg:py-20">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">Planes y precios</h2>
              <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                Oferta de lanzamiento para los primeros 100 clientes. Precio garantizado de por vida.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {plans.map(({ name, price, desc, features: feats, popular }) => (
                <div
                  key={name}
                  className={`relative border rounded-xl p-6 bg-white ${
                    popular ? 'border-blue-400 shadow-lg shadow-blue-100 ring-2 ring-blue-400' : 'border-slate-200'
                  }`}
                >
                  {popular && (
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 bg-blue-500 text-white text-xs font-bold rounded-full">
                      Mas popular
                    </span>
                  )}
                  <p className="font-bold text-slate-800 text-lg">{name}</p>
                  <p className="text-slate-500 text-sm mb-3">{desc}</p>
                  <p className="text-4xl font-bold text-slate-900 mb-1">
                    {price}€<span className="text-base font-normal text-slate-500">/mes</span>
                  </p>
                  <p className="text-xs text-slate-400 mb-5">+ IVA. Facturacion mensual.</p>
                  <ul className="space-y-2 mb-6">
                    {feats.map((f) => (
                      <li key={f} className="flex gap-2 text-sm text-slate-700">
                        <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                        {f}
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/register"
                    className={`block w-full text-center py-3 rounded-xl font-semibold transition-colors ${
                      popular
                        ? 'bg-blue-500 text-white hover:bg-blue-600'
                        : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                    }`}
                  >
                    Probar gratis 14 dias
                  </Link>
                </div>
              ))}
            </div>

            <p className="text-center text-slate-500 mt-6">
              <Link to="/precios" className="text-blue-500 hover:underline font-medium">
                Ver comparativa completa de planes y descuentos anuales →
              </Link>
            </p>
          </div>
        </section>

        {/* ======================== FAQ ======================== */}
        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Preguntas frecuentes sobre software de escandallos
            </h2>
          </div>

          <div className="space-y-4">
            {faqs.map(({ q, a }) => (
              <details key={q} className="border border-slate-200 rounded-xl overflow-hidden">
                <summary className="p-5 font-semibold text-slate-800 cursor-pointer hover:bg-slate-50 flex justify-between items-center list-none">
                  {q}
                  <span className="text-slate-400 text-xl ml-4 flex-shrink-0">+</span>
                </summary>
                <div className="px-5 pb-5 text-slate-600 leading-relaxed border-t border-slate-100 pt-4">{a}</div>
              </details>
            ))}
          </div>
        </section>

        {/* ======================== INTERNAL LINKS ======================== */}
        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
          <div className="p-6 bg-slate-50 rounded-xl border border-slate-200">
            <h3 className="font-bold text-slate-900 mb-3 flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-blue-500" /> Guias relacionadas
            </h3>
            <div className="grid sm:grid-cols-2 gap-3">
              {[
                { to: '/escandallo', label: 'Que es un escandallo: guia completa' },
                { to: '/como-hacer-escandallo', label: 'Como hacer un escandallo paso a paso' },
                { to: '/ejemplo-escandallo', label: '8 ejemplos reales de escandallos' },
                { to: '/escandallo-bebidas', label: 'Escandallo de bebidas y cocteles' },
                { to: '/calculadora-escandallo', label: 'Calculadora de escandallo online gratis' },
                { to: '/plantilla-escandallo-excel', label: 'Plantilla de escandallo en Excel (gratis)' },
                { to: '/food-cost', label: 'Food cost: que es y como calcularlo' },
                { to: '/calculadora-food-cost', label: 'Calculadora de food cost gratuita' },
              ].map(({ to, label }) => (
                <Link key={to} to={to} className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 hover:underline">
                  <ArrowRight className="w-3 h-3 flex-shrink-0" /> {label}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* ======================== FINAL CTA ======================== */}
        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
          <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-2xl p-8 sm:p-12 text-center text-white">
            <Zap className="w-12 h-12 mx-auto mb-4 opacity-90" />
            <h2 className="text-2xl sm:text-3xl font-bold mb-3">Empieza a controlar tus costes hoy</h2>
            <p className="text-blue-100 mb-6 max-w-xl mx-auto text-lg leading-relaxed">
              Prueba PLEKO gratis durante 14 dias. Calcula el coste real de toda tu carta, detecta recetas no rentables
              y toma decisiones con datos en vez de con intuicion.
            </p>
            <Link
              to="/register"
              className="inline-flex items-center gap-2 px-8 py-4 bg-white text-blue-600 font-bold rounded-xl hover:bg-blue-50 transition-colors shadow-lg"
            >
              Crear cuenta gratis
              <ArrowRight className="w-5 h-5" />
            </Link>
            <p className="mt-4 text-blue-200 text-sm">Sin tarjeta de credito. Configurado en 2 minutos. Cancela cuando quieras.</p>
          </div>
        </section>
      </div>
    </>
  );
}
